import { createAsyncThunk } from "@reduxjs/toolkit"
import { del, formDataPost, formDataPut, get, patch, post, put } from "helpers/api_helper"
import {
  SA_GET_ALL_TENANTS,
  SA_GET_TENANT_DETAILS,
  SA_CREATE_TENANT,
  SA_UPDATE_TENANT,
  SA_UPDATE_TENANT_STATUS,
  SA_GET_TENANT_INVOICES,
  SA_GET_TENANT_SUBSCRIPTION,
  SA_ASSIGN_TENANT_PLAN,
  SA_TENANT_LOGIN,
  SA_TENANT_MIGRATION_START,
  SA_TENANT_MIGRATION_STATUS,
  SA_TENANT_MIGRATION_CUTOVER,
  SA_GET_ALL_PLANS,
  SA_GET_PLAN_DETAILS,
  SA_CREATE_PLAN,
  SA_UPDATE_PLAN,
  SA_DELETE_PLAN,
  SA_UPDATE_PLAN_STATUS,
  SA_GET_ALL_SUBSCRIPTIONS,
  SA_GET_ALL_INVOICES,
  SA_DOWNLOAD_INVOICE,
  SA_GET_ALL_INTEGRATION_CATEGORIES,
  SA_CREATE_INTEGRATION_CATEGORY,
  SA_UPDATE_INTEGRATION_CATEGORY,
  SA_DELETE_INTEGRATION_CATEGORY,
  SA_UPDATE_INTEGRATION_CATEGORY_STATUS,
  SA_GET_ALL_INTEGRATIONS,
  SA_GET_INTEGRATIONS_CATALOG_TREE,
  SA_CREATE_INTEGRATION,
  SA_UPDATE_INTEGRATION,
  SA_DELETE_INTEGRATION,
  SA_UPDATE_INTEGRATION_STATUS,
  SA_GET_ALL_BRANDS,
  SA_GET_BRAND_LIST,
  SA_GET_BRAND_DETAILS,
  SA_ADD_BRAND,
  SA_UPDATE_BRAND,
  SA_DELETE_BRAND,
  SA_TOGGLE_BRAND,
  SA_GET_BRAND_NAMES_LIST,
  SA_GET_BRAND_PAYMENT_METHODS,
  SA_GET_TENANT_FULFILLMENT_INTEGRATIONS,
  SA_GET_DELIVERY_PROFILE_TEMPLATES,
} from "helpers/url_helper"

const pickId = payload =>
  payload?.id || payload?._id || payload?.tenantId || payload?.planId || payload?.invoiceId

const requireId = (payload, actionName) => {
  const id = pickId(payload)
  if (!id) {
    throw new Error(`${actionName} requires payload.id`)
  }
  return id
}

/** Map form-only plan fields to API fields; drop keys Joi rejects on create tenant. */
const sanitizeTenantCreatePayload = (payload = {}) => {
  const {
    planAssignmentMode,
    planPaymentReference,
    assignmentMode: explicitAssignmentMode,
    paymentReference: explicitPaymentReference,
    ...body
  } = payload

  const assignmentMode =
    explicitAssignmentMode || planAssignmentMode || "comped"
  const sanitized = { ...body, assignmentMode }

  const paymentReference = explicitPaymentReference || planPaymentReference
  if (assignmentMode === "manual_paid" && paymentReference) {
    sanitized.paymentReference = paymentReference
  }

  return sanitized
}

// ── TENANT MANAGEMENT ─────────────────────────────────────────────────────────

export const saGetAllTenants = createAsyncThunk(
  SA_GET_ALL_TENANTS,
  async payload => {
    const response = await get("superadmin/tenants/all", { params: payload || {} })
    return response
  }
)

export const saGetTenantDetails = createAsyncThunk(
  SA_GET_TENANT_DETAILS,
  async payload => {
    const id = requireId(payload, "SA_GET_TENANT_DETAILS")
    const response = await get(`superadmin/tenants/${id}`)
    return response
  }
)

export const saCreateTenant = createAsyncThunk(
  SA_CREATE_TENANT,
  async payload => {
    const response = await post(
      SA_CREATE_TENANT,
      sanitizeTenantCreatePayload(payload)
    )
    return response
  }
)

export const saUpdateTenant = createAsyncThunk(
  SA_UPDATE_TENANT,
  async payload => {
    const id = requireId(payload, "SA_UPDATE_TENANT")
    // Tenant id is only in the URL path; omit id/_id from JSON so the API body matches Joi and avoids confusion.
    const { id: _omitId, _id: _omitMongoId, ...body } = payload || {}
    const response = await put(`superadmin/tenants/${id}/update`, body)
    return response
  }
)

export const saStartTenantMigration = createAsyncThunk(
  SA_TENANT_MIGRATION_START,
  async payload => {
    const id = requireId(payload, "SA_TENANT_MIGRATION_START")
    const response = await post(`superadmin/tenants/${id}/migration/start`, {})
    return response
  }
)

export const saGetTenantMigrationStatus = createAsyncThunk(
  SA_TENANT_MIGRATION_STATUS,
  async payload => {
    const id = requireId(payload, "SA_TENANT_MIGRATION_STATUS")
    const response = await get(`superadmin/tenants/${id}/migration/status`)
    return response
  }
)

export const saCompleteTenantMigrationCutover = createAsyncThunk(
  SA_TENANT_MIGRATION_CUTOVER,
  async payload => {
    const id = requireId(payload, "SA_TENANT_MIGRATION_CUTOVER")
    const response = await post(`superadmin/tenants/${id}/migration/cutover`, {})
    return response
  }
)

export const saUpdateTenantStatus = createAsyncThunk(
  SA_UPDATE_TENANT_STATUS,
  async payload => {
    const id = requireId(payload, "SA_UPDATE_TENANT_STATUS")
    const { id: _omitId, _id: _omitMongoId, ...body } = payload || {}
    const response = await patch(`superadmin/tenants/${id}/status`, body)
    return response
  }
)

export const saGetTenantInvoices = createAsyncThunk(
  SA_GET_TENANT_INVOICES,
  async payload => {
    const id = requireId(payload, "SA_GET_TENANT_INVOICES")
    const response = await get(`superadmin/tenants/${id}/invoices`, {
      params: payload || {},
    })
    return response
  }
)

export const saGetTenantSubscription = createAsyncThunk(
  SA_GET_TENANT_SUBSCRIPTION,
  async payload => {
    const id = requireId(payload, "SA_GET_TENANT_SUBSCRIPTION")
    const response = await get(`superadmin/tenants/${id}/subscription`)
    return response
  }
)

export const saAssignTenantPlan = createAsyncThunk(
  SA_ASSIGN_TENANT_PLAN,
  async payload => {
    const id =
      payload?.id || payload?._id || payload?.tenantId
    if (!id) {
      throw new Error("SA_ASSIGN_TENANT_PLAN requires payload.id")
    }
    const { id: _omitId, _id: _omitMongoId, ...body } = payload || {}
    const response = await post(`superadmin/tenants/${id}/assign-plan`, body)
    return response
  }
)

export const saLoginToTenant = createAsyncThunk(
  SA_TENANT_LOGIN,
  async payload => {
    const id = requireId(payload, "SA_TENANT_LOGIN")
    const response = await post(`superadmin/tenants/${id}/login`, {})
    return response
  }
)

export const saRefreshTenantPermissions = createAsyncThunk(
  "superAdmin/saRefreshTenantPermissions",
  async payload => {
    const id = requireId(payload, "saRefreshTenantPermissions")
    const response = await post(`superadmin/tenants/${id}/refresh-permissions`, {})
    return response
  }
)

// ── PLAN MANAGEMENT ───────────────────────────────────────────────────────────

export const saGetAllPlans = createAsyncThunk(SA_GET_ALL_PLANS, async payload => {
  const response = await get("superadmin/plans/all", { params: payload || {} })
  return response
})

export const saGetPlanDetails = createAsyncThunk(
  SA_GET_PLAN_DETAILS,
  async payload => {
    const id = requireId(payload, "SA_GET_PLAN_DETAILS")
    const response = await get(`superadmin/plans/${id}`)
    return response
  }
)

export const saCreatePlan = createAsyncThunk(SA_CREATE_PLAN, async payload => {
  const response = await post(SA_CREATE_PLAN, payload)
  return response
})

export const saUpdatePlan = createAsyncThunk(SA_UPDATE_PLAN, async payload => {
  const id = requireId(payload, "SA_UPDATE_PLAN")
  const { id: _planId, ...body } = payload || {}
  const response = await put(`superadmin/plans/${id}/update`, body)
  return response
})

export const saDeletePlan = createAsyncThunk(SA_DELETE_PLAN, async payload => {
  const id = requireId(payload, "SA_DELETE_PLAN")
  const response = await del(`superadmin/plans/${id}/delete`, payload)
  return response
})

export const saUpdatePlanStatus = createAsyncThunk(
  SA_UPDATE_PLAN_STATUS,
  async payload => {
    const id = requireId(payload, "SA_UPDATE_PLAN_STATUS")
    const { id: _omitId, _id: _omitMongoId, ...body } = payload || {}
    const response = await patch(`superadmin/plans/${id}/status`, body)
    return response
  }
)

// ── BILLING OVERSIGHT ─────────────────────────────────────────────────────────

export const saGetAllSubscriptions = createAsyncThunk(
  SA_GET_ALL_SUBSCRIPTIONS,
  async payload => {
    const response = await get("superadmin/billing/subscriptions", {
      params: payload || {},
    })
    return response
  }
)

export const saGetAllInvoices = createAsyncThunk(
  SA_GET_ALL_INVOICES,
  async payload => {
    const response = await get("superadmin/billing/invoices", {
      params: payload || {},
    })
    return response
  }
)

export const saDownloadInvoice = createAsyncThunk(
  SA_DOWNLOAD_INVOICE,
  async payload => {
    const id = requireId(payload, "SA_DOWNLOAD_INVOICE")
    const response = await get(`superadmin/billing/invoices/${id}/download`)
    return response
  }
)

// ── INTEGRATION CATEGORIES ────────────────────────────────────────────────────

export const saGetAllIntegrationCategories = createAsyncThunk(
  SA_GET_ALL_INTEGRATION_CATEGORIES,
  async payload => {
    const response = await get("superadmin/integration-categories/all", {
      params: payload || {},
    })
    return response
  }
)

export const saCreateIntegrationCategory = createAsyncThunk(
  SA_CREATE_INTEGRATION_CATEGORY,
  async payload => {
    const response = await post(SA_CREATE_INTEGRATION_CATEGORY, payload)
    return response
  }
)

export const saUpdateIntegrationCategory = createAsyncThunk(
  SA_UPDATE_INTEGRATION_CATEGORY,
  async payload => {
    const id = requireId(payload, "SA_UPDATE_INTEGRATION_CATEGORY")
    const { id: _categoryId, ...body } = payload || {}
    const response = await put(`superadmin/integration-categories/${id}/update`, body)
    return response
  }
)

export const saUpdateIntegrationCategoryStatus = createAsyncThunk(
  SA_UPDATE_INTEGRATION_CATEGORY_STATUS,
  async payload => {
    const id = requireId(payload, "SA_UPDATE_INTEGRATION_CATEGORY_STATUS")
    const { id: _omitId, _id: _omitMongoId, ...body } = payload || {}
    const response = await patch(`superadmin/integration-categories/${id}/status`, body)
    return response
  }
)

export const saDeleteIntegrationCategory = createAsyncThunk(
  SA_DELETE_INTEGRATION_CATEGORY,
  async payload => {
    const id = requireId(payload, "SA_DELETE_INTEGRATION_CATEGORY")
    const response = await del(`superadmin/integration-categories/${id}/delete`, payload)
    return response
  }
)

// ── INTEGRATIONS ──────────────────────────────────────────────────────────────

export const saGetAllIntegrations = createAsyncThunk(
  SA_GET_ALL_INTEGRATIONS,
  async payload => {
    const response = await get("superadmin/integrations/all", {
      params: payload || {},
    })
    return response
  }
)

export const saGetIntegrationsCatalogTree = createAsyncThunk(
  SA_GET_INTEGRATIONS_CATALOG_TREE,
  async (params = {}) => {
    const query = {}
    if (params && typeof params === "object") {
      if (params.all === true || params.all === "true") query.all = true
      if (params.categoryStatus) query.categoryStatus = params.categoryStatus
      if (params.integrationStatus) query.integrationStatus = params.integrationStatus
    }
    const response = await get("superadmin/integrations/catalog-tree", {
      params: Object.keys(query).length ? query : undefined,
    })
    return response
  }
)

export const saCreateIntegration = createAsyncThunk(
  SA_CREATE_INTEGRATION,
  async payload => {
    const response =
      payload instanceof FormData
        ? await formDataPost(SA_CREATE_INTEGRATION, payload)
        : await post(SA_CREATE_INTEGRATION, payload)
    return response
  }
)

export const saUpdateIntegration = createAsyncThunk(
  SA_UPDATE_INTEGRATION,
  async payload => {
    if (payload instanceof FormData) {
      const id = payload.get("id")
      if (!id) {
        throw new Error("SA_UPDATE_INTEGRATION requires payload.id in FormData")
      }
      const response = await formDataPut(`superadmin/integrations/${id}/update`, payload)
      return response
    }
    const id = requireId(payload, "SA_UPDATE_INTEGRATION")
    const { id: _integrationId, ...body } = payload || {}
    const response = await put(`superadmin/integrations/${id}/update`, body)
    return response
  }
)

export const saUpdateIntegrationStatus = createAsyncThunk(
  SA_UPDATE_INTEGRATION_STATUS,
  async payload => {
    const id = requireId(payload, "SA_UPDATE_INTEGRATION_STATUS")
    const { id: _omitId, _id: _omitMongoId, ...body } = payload || {}
    const response = await patch(`superadmin/integrations/${id}/status`, body)
    return response
  }
)

export const saDeleteIntegration = createAsyncThunk(
  SA_DELETE_INTEGRATION,
  async payload => {
    const id = requireId(payload, "SA_DELETE_INTEGRATION")
    const response = await del(`superadmin/integrations/${id}/delete`, payload)
    return response
  }
)

// ── BRAND MANAGEMENT ──────────────────────────────────────────────────────────

export const saGetBrandList = createAsyncThunk(SA_GET_BRAND_LIST, async payload => {
  const { tenantId, forTenantId, ...rest } = payload || {}
  const tid = forTenantId || tenantId || ""
  const qs = tid ? `?forTenantId=${tid}` : ""
  const response = await get(`${SA_GET_BRAND_LIST}${qs}`, rest)
  return response
})

export const saGetAllBrands = createAsyncThunk(SA_GET_ALL_BRANDS, async payload => {
  const { tenantId, forTenantId, ...rest } = payload || {}
  const tid = forTenantId || tenantId || ""
  const response = await post(SA_GET_ALL_BRANDS, { ...rest, forTenantId: tid })
  return response
})

export const saGetBrandNamesList = createAsyncThunk(
  SA_GET_BRAND_NAMES_LIST,
  async () => {
    const response = await get(SA_GET_BRAND_NAMES_LIST)
    return response
  }
)

export const saGetBrandPaymentMethods = createAsyncThunk(
  SA_GET_BRAND_PAYMENT_METHODS,
  async () => {
    const response = await get(SA_GET_BRAND_PAYMENT_METHODS)
    return response
  }
)

export const saAddBrand = createAsyncThunk(SA_ADD_BRAND, async payload => {
  const { tenantId, forTenantId, ...rest } = payload
  const response = await post(SA_ADD_BRAND, { ...rest, forTenantId: forTenantId || tenantId })
  return response
})

export const saGetBrandDetails = createAsyncThunk(
  SA_GET_BRAND_DETAILS,
  async ({ id, tenantId, forTenantId }) => {
    const tid = forTenantId || tenantId || ""
    const qs = tid ? `?forTenantId=${tid}` : ""
    const response = await get(`${SA_GET_BRAND_DETAILS}/${id}${qs}`)
    return response
  }
)

export const saUpdateBrand = createAsyncThunk(SA_UPDATE_BRAND, async payload => {
  const { id, tenantId, forTenantId, ...rest } = payload
  const response = await put(`${SA_UPDATE_BRAND}/${id}`, {
    ...rest,
    forTenantId: forTenantId || tenantId,
  })
  return response
})

export const saDeleteBrand = createAsyncThunk(SA_DELETE_BRAND, async ({ id, tenantId, forTenantId }) => {
  const response = await del(`${SA_DELETE_BRAND}/${id}`, { forTenantId: forTenantId || tenantId })
  return response
})

export const saToggleBrand = createAsyncThunk(SA_TOGGLE_BRAND, async ({ id, tenantId, forTenantId }) => {
  const response = await post(`${SA_TOGGLE_BRAND}/${id}`, { forTenantId: forTenantId || tenantId })
  return response
})

export const saGetTenantFulfillmentIntegrations = createAsyncThunk(
  SA_GET_TENANT_FULFILLMENT_INTEGRATIONS,
  async ({ tenantId }) => {
    const response = await get(`${SA_GET_TENANT_FULFILLMENT_INTEGRATIONS}?forTenantId=${tenantId}`)
    return response
  }
)

export const saGetDeliveryProfileTemplates = createAsyncThunk(
  SA_GET_DELIVERY_PROFILE_TEMPLATES,
  async ({ tenantId }) => {
    const response = await get(
      `${SA_GET_DELIVERY_PROFILE_TEMPLATES}?forTenantId=${tenantId}`
    )
    return response
  }
)
